import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Alert } from "@mui/material";
import { Print, ArrowBack, PictureAsPdf } from "@mui/icons-material";
import sailingService from "../../services/sailingService";

const AdminHouseBolPrint = () => {
    const { id, customerId } = useParams();
    const [searchParams] = useSearchParams();
    // Which of a multi-TIN customer's House Bills — blank for a
    // single-TIN customer, unchanged from before.
    const tin = searchParams.get("tin") || "";
    const navigate = useNavigate();
    const [html, setHtml] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        sailingService
            .getHouseBol(id, customerId, tin)
            .then(({ data }) => setHtml(data.data.html))
            .catch((err) => setError(err.response?.data?.message || "Failed to load House Bill of Lading"))
            .finally(() => setLoading(false));
    }, [id, customerId, tin]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await sailingService.getHouseBolPdf(id, customerId, tin);
            const disposition = response.headers?.["content-disposition"] || "";
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            const filename = filenameMatch ? filenameMatch[1] : "HBOL.pdf";

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setError("Failed to download House Bill of Lading");
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box className="no-print" sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button startIcon={<ArrowBack />} variant="outlined" size="small" onClick={() => navigate(-1)}>
                    Back
                </Button>
                <Button startIcon={<Print />} variant="contained" size="small" onClick={() => window.print()} disabled={!html}>
                    Print
                </Button>
                <Button
                    startIcon={downloading ? <CircularProgress size={16} /> : <PictureAsPdf />}
                    variant="outlined"
                    size="small"
                    onClick={handleDownload}
                    disabled={!html || downloading}
                >
                    Download PDF
                </Button>
            </Box>

            {error && (
                <Alert severity="error" className="no-print" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Always white page / dark text — this is a document meant to be
                printed on paper, never a dark-mode surface, and the
                server-generated HTML assumes a white backdrop of its own. */}
            {html && (
                <Box
                    sx={{ maxWidth: 800, mx: "auto", bgcolor: "#fff", color: "#0E1420", p: 2 }}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            )}

            <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
      `}</style>
        </Box>
    );
};

export default AdminHouseBolPrint;
